import { useEffect } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useNavigationLock } from '../../../../providers/NavigationLockProvider';
import type { PracticeDetail } from '../../../../services/practiceDetail';
import { PRACTICE_FIELD_DEFS } from './fieldDefinitions';
import { CreateBillComCustomerAction } from './CreateBillComCustomerAction';
import { RequestSparkAccountAction } from './RequestSparkAccountAction';
import { IteroConnectionField } from './IteroConnectionField';
import { SparkCredsAction } from './SparkCredsAction';
import { FindOrCreateDmAction } from './dm/FindOrCreateDmAction';
import { SaveBar } from './SaveBar';
import { DetailNav } from './DetailNav';
import { DetailAside } from './DetailAside';
import { type DetailSectionId } from './detailSections';
import { InfoSection } from './InfoSection';
import { AccountSection } from './AccountSection';
import { PracticeFilesSection } from './files/PracticeFilesSection';
import { PracticeBetaAccessSection } from './beta/PracticeBetaAccessSection';
import type { usePracticeDetailsForm } from './usePracticeDetailsForm';

type PracticeDetailsForm = ReturnType<typeof usePracticeDetailsForm>;

const DM_ID_FIELD_KEYS = ['dmPracticeId', 'dmDoctorId'];

interface DetailsTabProps {
    form: PracticeDetailsForm;
    practice: PracticeDetail;
    activeSection: DetailSectionId;
    onSectionChange: (section: DetailSectionId) => void;
    /**
     * Shared by ErrorBanner's "Fix X" and DetailAside's "Needs attention"
     * rows - both need to land on a field that may live in a section pane
     * other than the one currently showing. Owned by PracticeDetail (see its
     * scrollToFieldKeyed), which switches sections first when needed, then
     * scrolls once the target pane has mounted.
     */
    onFixField: (fieldKey: string) => void;
}

interface ErrorBannerProps {
    message: string | null;
    failedFieldKeys: string[];
    onFix: (key: string) => void;
}

function ErrorBanner({ message, failedFieldKeys, onFix }: ErrorBannerProps) {
    const title =
        failedFieldKeys.length === 1
            ? `Nothing was saved - 1 field needs fixing`
            : failedFieldKeys.length > 1
              ? `Nothing was saved - ${failedFieldKeys.length} fields need fixing`
              : 'Nothing was saved';

    return (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-red-800">{title}</p>
                {message && <p className="text-sm text-red-700 mt-1">{message}</p>}
                <p className="text-sm text-red-700 mt-1">
                    Every other pending change is kept below. Nothing was lost.
                </p>
                {failedFieldKeys.length === 1 && (
                    <div className="flex gap-2.5 mt-3">
                        <button
                            type="button"
                            onClick={() => onFix(failedFieldKeys[0])}
                            className="bg-white text-gray-800 border border-gray-300 px-3.5 h-9 rounded-lg hover:bg-gray-50 font-semibold text-sm"
                        >
                            Fix {PRACTICE_FIELD_DEFS[failedFieldKeys[0]]?.label ?? 'field'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export function DetailsTab({
    form,
    practice,
    activeSection,
    onSectionChange,
    onFixField
}: DetailsTabProps) {
    const practiceId = practice.id;
    const { lock, unlock } = useNavigationLock();

    // Block navigation only while the atomic PATCH is actually in flight -
    // this mirrors TaskSurface's "non-interruptible operation" usage of the
    // same lock. A merely-dirty (not yet saving) form intentionally does NOT
    // lock: the lock's overlay swallows every click, which would also block
    // the Save/Discard buttons the user needs to resolve the dirty state.
    useEffect(() => {
        if (form.phase !== 'saving') return;
        lock();
        return () => unlock();
    }, [form.phase, lock, unlock]);

    // Guard hard navigation (refresh/close/typed URL) whenever edits are
    // pending, independent of the lock above.
    useEffect(() => {
        if (!form.isDirty) return;
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [form.isDirty]);

    const fieldsDisabled = form.phase === 'saving';

    // Billing email falls back to the practice's general email, same
    // fallback the backend itself applies when `email` isn't overridden in
    // the request body (see bill-com.service.ts) - prefilling with it here
    // just means the modal usually opens already matching what a blank
    // submission would produce.
    const billComAction = (
        <CreateBillComCustomerAction
            practiceId={practiceId}
            prefill={{
                name: form.values.name ?? '',
                email: form.values.billingEmail?.trim()
                    ? form.values.billingEmail
                    : (form.values.email ?? ''),
                phone: form.values.phone ?? '',
                address1: form.values.address ?? '',
                city: form.values.city ?? '',
                state: form.values.state ?? '',
                zip: form.values.zip ?? '',
                country: form.values.country ?? ''
            }}
        />
    );

    const dmAction = (label: string) => (
        <FindOrCreateDmAction
            practiceId={practiceId}
            label={label}
            prefillSource={form.values}
            onLinked={() => form.discardFieldEdits(DM_ID_FIELD_KEYS)}
        />
    );

    const sparkAccountAction = <RequestSparkAccountAction practiceId={practiceId} />;
    const sparkCredsAction = <SparkCredsAction practiceId={practiceId} />;
    const iteroConnectionField = (
        <IteroConnectionField practiceId={practiceId} practiceName={practice.name} />
    );

    const gridProps = {
        values: form.values,
        fieldState: form.fieldState,
        fieldError: form.fieldError,
        disabled: fieldsDisabled,
        onFieldChange: form.onFieldChange
    };

    return (
        <div className="flex-1 min-h-0 flex flex-col gap-4">
            {form.phase === 'error' && (
                <ErrorBanner
                    message={form.bannerMessage}
                    failedFieldKeys={form.failedFieldKeys}
                    onFix={onFixField}
                />
            )}

            <div className="flex items-stretch gap-5 flex-1 min-h-0 overflow-hidden">
                <DetailNav
                    activeId={activeSection}
                    onSelect={onSectionChange}
                    values={form.values}
                    practiceId={practiceId}
                    updatedAt={practice.updatedAt}
                />

                {/* The save bar sits at the top of the content column (not a
                    sticky-bottom bar) so dirty state stays visible no matter
                    which section pane is active; while clean it renders
                    nothing and the rail's Complete block carries the
                    last-edited fact instead. */}
                <div className="flex-1 min-w-0 min-h-0 overflow-y-auto scrollbar-hide flex flex-col gap-3.5">
                    <SaveBar
                        phase={form.phase}
                        dirtyCount={form.dirtyCount}
                        dirtyLabels={form.dirtyLabels}
                        onSave={form.onSave}
                        onDiscard={form.onDiscard}
                    />

                    {activeSection === 'info' && (
                        <>
                            <InfoSection
                                {...gridProps}
                                riskUpdatedAt={practice.risk?.riskUpdatedAt}
                            />
                            <AccountSection
                                {...gridProps}
                                billComAction={billComAction}
                                dmLinkAction={dmAction('Find or create in DM')}
                                dmChangeAction={dmAction('Change')}
                                sparkAccountAction={sparkAccountAction}
                                sparkCredsAction={sparkCredsAction}
                                iteroConnectionField={iteroConnectionField}
                            />
                        </>
                    )}
                    {activeSection === 'files' && <PracticeFilesSection practiceId={practiceId} />}
                    {activeSection === 'beta' && (
                        <PracticeBetaAccessSection practiceId={practiceId} />
                    )}
                </div>

                <DetailAside practice={practice} values={form.values} onJumpToField={onFixField} />
            </div>
        </div>
    );
}
